import React, { useState, useEffect, useContext } from 'react';
import styles from "../../styles/pages/AllowedIps/AllowedIpsPage.module.css";
import { getAllowedIpsApi } from '../../utils/api/AllowedIP/allowedIpsApi';
import { deleteAllowedIpApi } from '../../utils/api/AllowedIP/deleteAllowedIpApi';
import { LoadingCircle } from '../../components/UI/Custom/LoadingCircle';
import { AddAllowedIpModal } from './components/AddAllowedIpModal';
import { EditAllowedIpModal } from './components/EditAllowedIpModal';
import { ConfirmationModal } from '../../components/UI/Custom/ConfirmationModal';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit'; 
import DeleteIcon from '@mui/icons-material/Delete'; 
import { LangContext } from '../../context/LangContext';
import { ThemeContext } from '../../context/ThemeContext';
import { Title } from '../../components/UI/Custom/Title';

const translations = {
    ru: {
        title: 'Управление IP-адресами',
        addIp: 'Добавить IP',
        id: 'ID',
        ipAddress: 'IP Адрес',
        createdAt: 'Дата создания',
        updatedAt: 'Дата обновления',
        actions: 'Действия',
        emptyList: 'Список IP пуст.',
        edit: 'Изменить',
        delete: 'Удалить',
        confirmTitle: 'Подтверждение удаления',
        confirmMessage: (id) => `Вы действительно хотите удалить IP-адрес с ID: #${id}? Это действие необратимо.`,
        confirmText: 'Да, удалить',
        cancelText: 'Нет, отмена',
        page: 'Страница'
    },
    en: {
        title: 'IP Address Management',
        addIp: 'Add IP',
        id: 'ID',
        ipAddress: 'IP Address',
        createdAt: 'Creation Date',
        updatedDate: 'Update Date',
        actions: 'Actions',
        emptyList: 'IP list is empty.',
        edit: 'Edit',
        delete: 'Delete',
        confirmTitle: 'Deletion Confirmation',
        confirmMessage: (id) => `Are you sure you want to delete the IP address with ID: #${id}? This action is irreversible.`,
        confirmText: 'Yes, delete',
        cancelText: 'No, cancel',
        page: 'Page'
    }
};

export const AllowedIpsPage = () => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const t = translations[langSelected] || translations.ru;

    const [ips, setIps] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, per_page: 15, total: 0 });
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [ipToEdit, setIpToEdit] = useState(null);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [ipToDeleteId, setIpToDeleteId] = useState(null);

    const loadIps = async (currentPage = pagination.page, currentPerPage = pagination.per_page) => {
        setIsLoading(true);
        try {
            const data = await getAllowedIpsApi(currentPage, currentPerPage);
            setIps(data.items);
            setPagination({
                page: data.page,
                per_page: data.per_page,
                total: data.total
            });
        } catch (error) {
            console.error('Ошибка при загрузке IP:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadIps();
    }, []);

    const handlePageChange = (newPage) => {
        console.log(`Переход на страницу ${newPage}`);
    };

    const handleAdd = () => {
        setIsAddModalOpen(true);
    };

    const handleAddIpModalClose = () => {
        setIsAddModalOpen(false);
    };

    const handleEdit = (ipItem) => {
        setIpToEdit(ipItem);
    };

    const handleEditIpModalClose = () => {
        setIpToEdit(null);
    };

    const handleDelete = (id) => {
        setIpToDeleteId(id);
        setIsDeleteConfirmOpen(true);
    };

    const handleCloseDeleteConfirm = () => {
        setIsDeleteConfirmOpen(false);
        setIpToDeleteId(null);
    };

    const confirmDelete = async () => {
        if (!ipToDeleteId) return;

        setIsDeleteConfirmOpen(false);
        setIsLoading(true);

        try {
            await deleteAllowedIpApi(ipToDeleteId);
            await loadIps();
        } catch (error) {
            console.error('Ошибка при удалении IP:', error);
        } finally {
            setIsLoading(false);
            setIpToDeleteId(null);
        }
    };

    if (isLoading) {
        return (
            <div className={`${styles.loadingBlock} ${isDark ? styles.darkLoadingBlock : ''}`}>
                <LoadingCircle isDark={isDark} />
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <Title h2={t.title} activeSpan={true} />

            <div className={`${styles.content} ${isDark ? styles.darkContent : ''}`}>
                <div className={styles.topBar} style={{ justifyContent: 'flex-end' }}>
                    <div className={styles.action}>
                        <div className={`${styles.addButtonContainer} ${isDark ? styles.darkAddButtonContainer : ''}`}>
                            <button onClick={handleAdd} className={`${styles.addButton} ${isDark ? styles.darkAddButton : ''}`}>
                                <AddIcon style={{ fontSize: '1.2rem' }} />
                                <span className={styles.addButtonText}>{t.addIp}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead className={`${styles.tableHeader} ${isDark ? styles.darkTableHeader : ''}`}>
                            <tr>
                                <th>{t.id}</th>
                                <th>{t.ipAddress}</th>
                                <th>{t.createdAt}</th>
                                <th>{t.updatedAt}</th>
                                <th>{t.actions}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ips.length > 0 ? (
                                ips.map((item) => (
                                    <tr key={item.id} className={`${styles.tableRow} ${isDark ? styles.darkTableRow : ''}`}>
                                        <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{item.id}</td>
                                        <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{item.ip}</td>
                                        <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{new Date(item.created_at).toLocaleDateString()}</td>
                                        <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{new Date(item.updated_at).toLocaleDateString()}</td>
                                        <td className={`${styles.tableCell} ${styles.actionButtons} ${isDark ? styles.darkTableCell : ''}`}>
                                            
                                            <button onClick={() => handleEdit(item)} className={`${styles.editButton} ${isDark ? styles.darkEditButton : ''}`} title={t.edit}>
                                                <EditIcon style={{ fontSize: '1.1rem', marginRight: '5px' }} /> 
                                                {t.edit}
                                            </button>
                                            
                                            <button onClick={() => handleDelete(item.id)} className={`${styles.deleteButton} ${isDark ? styles.darkDeleteButton : ''}`} title={t.delete}>
                                                <DeleteIcon style={{ fontSize: '1.1rem', marginRight: '5px' }} /> 
                                                {t.delete}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className={`${styles.noLeadsMessage} ${isDark ? styles.darkNoLeadsMessage : ''}`}>{t.emptyList}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {pagination.total > pagination.per_page && (
                    <div className={`${styles.pagination} ${isDark ? styles.darkPagination : ''}`}>
                        <p>{t.page} {pagination.page} из {Math.ceil(pagination.total / pagination.per_page)}</p>
                    </div>
                )}
            </div>

            {isAddModalOpen && (
                <AddAllowedIpModal
                    handleAddIpModalClose={handleAddIpModalClose}
                    fetchIps={loadIps}
                />
            )}

            {ipToEdit && (
                <EditAllowedIpModal
                    handleEditIpModalClose={handleEditIpModalClose}
                    fetchIps={loadIps}
                    ipToEdit={ipToEdit}
                />
            )}

            <ConfirmationModal
                isOpen={isDeleteConfirmOpen}
                onClose={handleCloseDeleteConfirm}
                onConfirm={confirmDelete}
                title={t.confirmTitle}
                message={t.confirmMessage(ipToDeleteId)}
                confirmText={t.confirmText}
                cancelText={t.cancelText}
            />
        </div>
    );
};